import { useState, useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowUp, ArrowDown, Save, Lock, Layers, Check, Trash2, Copy, Monitor, Laptop, Tablet, Smartphone, Eye, EyeOff, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { useNavigation } from "@/contexts/NavigationContext";
import { WORKSPACES, HOME_SIDEBAR } from "@/lib/navRegistry";

const HOME_CORE = HOME_SIDEBAR.slice(0, 10);   // configurable Home items
const SB_STATES = [{ v: "expanded", l: "Expanded" }, { v: "compact", l: "Compact" }, { v: "hidden", l: "Hidden" }];

const ZONES = [{ v: "top", l: "Top" }, { v: "more", l: "More" }, { v: "hidden", l: "Hidden" }];
const PRIOS = ["high", "medium", "low"];
const DEVICES = [
  { k: "desktop", l: "Large Desktop", w: 1440, icon: Monitor },
  { k: "laptop", l: "Laptop", w: 1120, icon: Laptop },
  { k: "tablet", l: "Tablet", w: 900, icon: Tablet },
  { k: "mobile", l: "Mobile", w: 420, icon: Smartphone },
];
const CORE = ["home", "tasks", "settings"]; // core/fixed — cannot be hidden

function previewSplit(list, width) {
  const top = list.filter((x) => (x.zone || "top") === "top");
  const more = list.filter((x) => x.zone === "more");
  if (width < 1024) return { inline: [], more: [...top, ...more], switcher: true };
  if (width < 1280) return { inline: top.filter((x) => x.priority === "high"), more: [...top.filter((x) => x.priority !== "high"), ...more], switcher: false };
  return { inline: top, more, switcher: false };
}

export default function NavigationBuilder() {
  const { config, ctx, save, reload } = useNavigation();
  const [draft, setDraft] = useState({ landing: "/", workspaces: {} });
  const [order, setOrder] = useState(WORKSPACES.map((w) => w.id));
  const [templates, setTemplates] = useState([]);
  const [device, setDevice] = useState("laptop");
  const [sbState, setSbState] = useState("expanded");
  const [homeOrder, setHomeOrder] = useState(HOME_CORE.map((i) => i.id));
  const [homeItems, setHomeItems] = useState({});

  useEffect(() => {
    setDraft({ landing: config.landing || "/", workspaces: { ...(config.workspaces || {}) } });
    if (config.order) setOrder(config.order);
    const home = config.home || {};
    setHomeOrder(home.order && home.order.length ? home.order.filter((id) => HOME_CORE.some((i) => i.id === id)) : HOME_CORE.map((i) => i.id));
    setHomeItems({ ...(home.items || {}) });
  }, [config]);

  useEffect(() => { if (ctx.isAdmin) apiFetch("/navigation/templates").then(setTemplates).catch(() => {}); }, [ctx.isAdmin]);

  if (!ctx.isAdmin) {
    return (
      <Card className="mx-auto mt-10 max-w-md p-8 text-center shadow-soft" data-testid="nav-builder-denied">
        <Lock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h2 className="font-heading text-xl font-semibold">Admins only</h2>
        <p className="mt-1 text-sm text-muted-foreground">Only Owner, Super Admin or an Admin with settings permission can restructure organisation navigation.</p>
      </Card>
    );
  }

  const merged = order.map((id) => {
    const base = WORKSPACES.find((w) => w.id === id);
    return { ...base, ...(draft.workspaces[id] || {}), label: draft.workspaces[id]?.label || base.label };
  });

  const setWs = (id, patch) => setDraft((d) => ({ ...d, workspaces: { ...d.workspaces, [id]: { ...(d.workspaces[id] || {}), ...patch } } }));
  const move = (i, dir) => { const n = [...order]; const j = i + dir; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setOrder(n); };

  const homeMerged = homeOrder.map((id) => {
    const base = HOME_CORE.find((i) => i.id === id);
    const ov = homeItems[id] || {};
    return { ...base, ...ov, label: ov.label || base.label, adminOnly: ov.adminOnly ?? base.adminOnly };
  });
  const setHomeItem = (id, patch) => setHomeItems((m) => ({ ...m, [id]: { ...(m[id] || {}), ...patch } }));
  const moveHome = (i, dir) => { const n = [...homeOrder]; const j = i + dir; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setHomeOrder(n); };

  const persist = async () => {
    try { await save({ ...draft, order, home: { order: homeOrder, items: homeItems } }); toast.success("Navigation saved for the organisation"); reload(); }
    catch (e) { toast.error(e.message); }
  };
  const applyTemplate = async (t) => {
    try { await apiFetch(`/navigation/apply/${t.id}`, { method: "POST" }); toast.success(`Applied “${t.name}”`); reload(); }
    catch (e) { toast.error(e.message); }
  };
  const duplicateCurrent = async () => {
    const name = prompt("Template name", "My navigation");
    if (!name) return;
    try { await apiFetch("/navigation/templates", { method: "POST", body: JSON.stringify({ name, description: "Saved from current navigation", config: { ...draft, order } }) }); setTemplates(await apiFetch("/navigation/templates")); toast.success("Template created"); }
    catch (e) { toast.error(e.message); }
  };
  const archive = async (t) => { try { await apiFetch(`/navigation/templates/${t.id}/archive`, { method: "PUT" }); setTemplates(await apiFetch("/navigation/templates")); } catch (e) { toast.error(e.message); } };

  const split = previewSplit(merged.filter((x) => x.zone !== "hidden"), DEVICES.find((d) => d.k === device).w);
  const isMobilePreview = device === "mobile";

  return (
    <div className="animate-fade-up" data-testid="navigation-builder">
      <PageHeader eyebrow="Settings" title="Navigation"
        subtitle="Admin-controlled organisation navigation. Placement is presentation only — it never changes permissions."
        actions={<Button className="gap-1.5" onClick={persist} data-testid="save-navigation"><Save className="h-4 w-4" /> Save</Button>} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* builder */}
        <div className="space-y-4 lg:col-span-2">
          <Card className="p-4 shadow-soft">
            <div className="mb-3 flex items-center gap-2"><Label className="text-xs">Default landing page</Label>
              <Select value={draft.landing} onValueChange={(v) => setDraft((d) => ({ ...d, landing: v }))}>
                <SelectTrigger className="h-8 w-56" data-testid="landing-select"><SelectValue /></SelectTrigger>
                <SelectContent>{WORKSPACES.map((w) => <SelectItem key={w.id} value={w.route}>{w.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              {merged.map((w, i) => {
                const core = CORE.includes(w.id);
                return (
                  <div key={w.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-2.5" data-testid={`nav-item-${w.id}`}>
                    <div className="flex flex-col">
                      <button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-foreground" data-testid={`up-${w.id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-foreground" data-testid={`down-${w.id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                    </div>
                    <w.icon className="h-4 w-4 text-primary" />
                    <Input value={w.label} onChange={(e) => setWs(w.id, { label: e.target.value })} className="h-8 w-40" data-testid={`label-${w.id}`} />
                    {core && <span className="flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] text-secondary-foreground"><Lock className="h-3 w-3" /> Core</span>}
                    <Select value={w.zone || "top"} onValueChange={(v) => setWs(w.id, { zone: v })}>
                      <SelectTrigger className="h-8 w-24" data-testid={`zone-${w.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ZONES.filter((z) => !(core && z.v === "hidden")).map((z) => <SelectItem key={z.v} value={z.v}>{z.l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={w.priority || "medium"} onValueChange={(v) => setWs(w.id, { priority: v })}>
                      <SelectTrigger className="h-8 w-24" data-testid={`prio-${w.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIOS.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Home sidebar */}
          <Card className="p-4 shadow-soft" data-testid="home-sidebar-builder">
            <div className="mb-1 flex items-center gap-2">
              <HomeIcon className="h-4 w-4 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Home sidebar</h3>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
              Order, rename, show/hide and set who can see each Home item. Placement is presentation only — permissions are unchanged, and users only ever see items they’re allowed to open.
            </p>
            <div className="space-y-2">
              {homeMerged.map((it, i) => (
                <div key={it.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-2.5" data-testid={`home-item-${it.id}`}>
                  <div className="flex flex-col">
                    <button onClick={() => moveHome(i, -1)} className="text-muted-foreground hover:text-foreground" data-testid={`home-up-${it.id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button onClick={() => moveHome(i, 1)} className="text-muted-foreground hover:text-foreground" data-testid={`home-down-${it.id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                  </div>
                  <it.icon className="h-4 w-4 text-primary" />
                  <Input value={it.label} onChange={(e) => setHomeItem(it.id, { label: e.target.value })} className="h-8 w-44" data-testid={`home-label-${it.id}`} />
                  <Select value={it.adminOnly ? "admins" : "everyone"} onValueChange={(v) => setHomeItem(it.id, { adminOnly: v === "admins" })}>
                    <SelectTrigger className="h-8 w-32" data-testid={`home-role-${it.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="everyone">Everyone</SelectItem><SelectItem value="admins">Admins only</SelectItem></SelectContent>
                  </Select>
                  <button
                    onClick={() => setHomeItem(it.id, { hidden: !it.hidden })}
                    data-testid={`home-visibility-${it.id}`}
                    className={cn("ml-auto flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors",
                      it.hidden ? "border-border text-muted-foreground" : "border-accent/40 text-foreground")}>
                    {it.hidden ? <><EyeOff className="h-3.5 w-3.5" /> Hidden</> : <><Eye className="h-3.5 w-3.5" /> Visible</>}
                  </button>
                </div>
              ))}
            </div>
          </Card>

          {/* responsive preview */}
          <Card className="p-4 shadow-soft" data-testid="nav-preview">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-heading text-lg font-semibold">Responsive preview</h3>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border border-border p-0.5" data-testid="preview-sidebar-state">
                  {SB_STATES.map((s) => (
                    <button key={s.v} onClick={() => setSbState(s.v)} data-testid={`preview-sb-${s.v}`}
                      className={cn("rounded-md px-2 py-1 text-xs", sbState === s.v ? "bg-secondary text-foreground" : "text-muted-foreground")}>{s.l}</button>
                  ))}
                </div>
                <div className="flex rounded-lg border border-border p-0.5">
                  {DEVICES.map((d) => (
                    <button key={d.k} onClick={() => setDevice(d.k)} title={d.l} data-testid={`preview-${d.k}`}
                      className={cn("flex items-center gap-1 rounded-md px-2 py-1 text-xs", device === d.k ? "bg-secondary text-foreground" : "text-muted-foreground")}>
                      <d.icon className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* top nav row */}
            <div className="rounded-t-lg border border-border bg-muted/30 p-3">
              {split.switcher ? (
                <div className="flex items-center gap-2 text-sm"><span className="rounded-md bg-secondary px-2 py-1">Workspace ▾</span><span className="text-muted-foreground">+ {split.more.length} in switcher</span></div>
              ) : (
                <div className="flex flex-wrap items-center gap-1.5">
                  {split.inline.map((x) => <span key={x.id} className="rounded-md bg-secondary px-2 py-1 text-xs">{x.label}</span>)}
                  {split.more.length > 0 && <span className="rounded-md border border-dashed border-border px-2 py-1 text-xs text-muted-foreground">More ▾ ({split.more.length})</span>}
                </div>
              )}
            </div>
            {/* sidebar + workspace row */}
            <div className="flex min-h-[120px] rounded-b-lg border border-t-0 border-border">
              {isMobilePreview ? (
                <div className="flex-1 p-3 text-xs text-muted-foreground">Contextual navigation opens as a touch drawer on mobile — it never consumes screen width.</div>
              ) : sbState === "hidden" ? (
                <>
                  <div className="flex w-8 items-center justify-center border-r border-border bg-sidebar text-sidebar-fg/60">›</div>
                  <div className="flex-1 p-3 text-xs text-muted-foreground">Sidebar hidden — workspace uses the full width.</div>
                </>
              ) : sbState === "compact" ? (
                <>
                  <div className="flex w-12 flex-col items-center gap-2 border-r border-border bg-sidebar py-3">
                    {homeMerged.filter((x) => !x.hidden).slice(0, 6).map((x) => <x.icon key={x.id} className="h-4 w-4 text-sidebar-fg/80" />)}
                  </div>
                  <div className="flex-1 p-3 text-xs text-muted-foreground">Icon rail with tooltips on hover/focus.</div>
                </>
              ) : (
                <>
                  <div className="w-40 space-y-1 border-r border-border bg-sidebar p-2">
                    {homeMerged.filter((x) => !x.hidden).slice(0, 6).map((x) => (
                      <div key={x.id} className="flex items-center gap-2 rounded px-2 py-1 text-xs text-sidebar-fg/80"><x.icon className="h-3.5 w-3.5" /> {x.label}</div>
                    ))}
                  </div>
                  <div className="flex-1 p-3 text-xs text-muted-foreground">Expanded sidebar — icons + labels.</div>
                </>
              )}
            </div>
          </Card>
        </div>

        {/* templates */}
        <Card className="h-fit p-4 shadow-soft" data-testid="nav-templates">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Templates</h3>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={duplicateCurrent} data-testid="save-template"><Copy className="h-3.5 w-3.5" /> Save current</Button>
          </div>
          <div className="space-y-2">
            {templates.map((t) => (
              <div key={t.id} className="rounded-lg border border-border p-3" data-testid={`nav-template-${t.id}`}>
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" />
                  <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{t.name}{t.is_preset && <span className="ml-1 text-[10px] text-muted-foreground">Preset</span>}</div>
                    <div className="truncate text-xs text-muted-foreground">{t.description}</div></div>
                </div>
                <div className="mt-2 flex gap-1.5">
                  <Button size="sm" className="h-7 gap-1" onClick={() => applyTemplate(t)} data-testid={`apply-template-${t.id}`}><Check className="h-3 w-3" /> Apply</Button>
                  {!t.is_preset && <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => archive(t)} data-testid={`archive-template-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
